// One-tap "Email snapshot" button for the Cash-flow forecast card.
// Grabs the current forecast's numbers and posts to /api/snapshots/autoshare
// which persists a public snapshot doc, sends the email, and remembers the
// address so the second tap is truly one tap.
//
// If the user has no saved autoshare_email yet (first-ever use), we open
// a tiny inline modal to capture the address — after that, the address is
// pre-filled and the modal shows it as read-only with a "change" option.

import { useState } from "react";
import { Mail, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";

// Map the human StatusBadge label the Dashboard passes in ("Safe",
// "Attention", "Warning") to the enum the backend expects.
function statusLabelToEnum(label) {
  const norm = String(label || "").toLowerCase();
  if (norm.startsWith("warn") || norm.startsWith("short")) return "short";
  if (norm.startsWith("atten") || norm.startsWith("tight")) return "tight";
  return "safe";
}

export default function EmailSnapshotButton({
  viewMode = "pay-period",
  periodStart,
  periodEnd,
  statusLabel = "Safe",
  startBalance = 0,
  incomingTotal = 0,
  billsTotal = 0,
  endBalance = 0,
  billCount = 0,
}) {
  const { user, refresh } = useAuth();
  const savedEmail = user?.autoshare_email || "";
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(savedEmail || user?.email || "");
  const [sending, setSending] = useState(false);

  const send = async (targetEmail) => {
    const clean = (targetEmail || "").trim();
    if (!clean) {
      toast.error("Enter an email address first.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/snapshots/autoshare", {
        email: clean,
        view_mode: viewMode,
        period_start: periodStart,
        period_end: periodEnd,
        status: statusLabelToEnum(statusLabel),
        start_balance: Number(startBalance || 0),
        incoming_total: Number(incomingTotal || 0),
        bills_total: Number(billsTotal || 0),
        end_balance: Number(endBalance || 0),
        bill_count: Number(billCount || 0),
      });
      await refresh();
      toast.success(
        data?.email_sent
          ? `Snapshot sent to ${clean}. It's safe to forward.`
          : `Snapshot created but the email couldn't be sent. Copy the link from your inbox instead.`,
      );
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const onClick = () => {
    // If we already remember an email, one-tap send directly. Otherwise
    // pop the modal to collect it.
    if (savedEmail) {
      send(savedEmail);
    } else {
      setEmail(user?.email || "");
      setOpen(true);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        disabled={sending || !periodStart || !periodEnd}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#D8CFB9] bg-white text-[#2C302E] text-xs font-semibold hover:bg-[#F5F0E4] transition-colors disabled:opacity-50"
        title={savedEmail ? `Send to ${savedEmail}` : "Email a snapshot"}
        data-testid="email-snapshot-btn"
      >
        <Mail size={13} />
        {sending ? "Sending…" : savedEmail ? "Email snapshot" : "Email snapshot…"}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-[#2C302E]/40 flex items-center justify-center p-4"
          onClick={() => !sending && setOpen(false)}
          data-testid="email-snapshot-modal"
        >
          <div
            className="w-full max-w-sm bg-white rounded-2xl p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-medium text-[#2C302E]">Email this snapshot</h3>
              <button
                type="button"
                onClick={() => setOpen(false)}
                disabled={sending}
                className="btn-ghost !p-1.5"
                aria-label="Close"
                data-testid="email-snapshot-close"
              >
                <X size={18} />
              </button>
            </div>
            <p className="text-sm text-[#59605C] mb-4">
              We&rsquo;ll send a short recap with the numbers inline plus a link your partner can open in a browser. Your address is saved for next time.
            </p>
            <label className="block">
              <span className="block text-xs font-semibold uppercase tracking-widest text-[#59605C] mb-1">Send to</span>
              <input
                type="email"
                autoFocus
                className="input-soft w-full"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="partner@example.com"
                onKeyDown={(e) => { if (e.key === "Enter" && !sending) send(email); }}
                data-testid="email-snapshot-input"
              />
            </label>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                className="btn-ghost"
                onClick={() => setOpen(false)}
                disabled={sending}
                data-testid="email-snapshot-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={() => send(email)}
                disabled={sending}
                data-testid="email-snapshot-send"
              >
                {sending ? "Sending…" : "Send"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
